/**
 * Sample Product Cart Validation
 *
 * Rule: products tagged "sample" are limited to 1 unit per flavour (per product)
 * in the cart. Customers may combine several different samples, but never more
 * than one unit of the same sample.
 *
 * Constraints this implementation works around:
 * - Shopify's Cart AJAX API (/cart.js) does NOT expose product tags on line
 *   items, so cart lines are identified as samples via /products/{handle}.js
 *   (its response includes `tags`), cached per handle.
 * - Repeated adds of the same variant are merged into one line by Shopify and
 *   the quantities are summed. The submit interception blocks that up front
 *   using a synchronously available cart snapshot (an async check before
 *   preventDefault() would run too late), and validateCurrentCart() clamps
 *   anything that slips through (double-click races, quick-buy, stale tabs).
 */

class SampleCartValidator {
  constructor() {
    this.SAMPLE_TAG = 'sample';
    this.MAX_PER_SAMPLE = 1;
    this.routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.cartVariantIds = new Set();
    this.sampleHandleCache = new Map();
    this.isClamping = false;
    this.toastContainer = null;
    this.init();
  }

  init() {
    this.createToastContainer();
    this.interceptAddToCart();

    // product-form.js (add) and cart.js (quantity change) both publish this event
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.onCartChanged());
      subscribe(PUB_SUB_EVENTS.variantChange, () => this.updateSampleFormsUI());
    }

    this.onCartChanged();
  }

  /* ------------------------------------------------------------------ *
   * Cart state
   * ------------------------------------------------------------------ */

  async onCartChanged() {
    const cart = await this.getCurrentCart();
    if (!cart) return;

    this.cartVariantIds = new Set(cart.items.map((item) => String(item.variant_id)));
    this.updateSampleFormsUI();
    this.validateCurrentCart(cart);
  }

  async getCurrentCart() {
    try {
      const response = await fetch(`${this.routesRoot}cart.js`);
      return await response.json();
    } catch (error) {
      console.error('[sample-cart-validation] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * /cart.js does not expose tags, so resolve them via the product endpoint.
   * Cached per handle; only ever called for lines that exceed the limit.
   */
  isSampleHandle(handle) {
    if (!handle) return Promise.resolve(false);

    if (!this.sampleHandleCache.has(handle)) {
      const lookup = fetch(`${this.routesRoot}products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => Boolean(product && product.tags && product.tags.includes(this.SAMPLE_TAG)))
        .catch(() => false);
      this.sampleHandleCache.set(handle, lookup);
    }

    return this.sampleHandleCache.get(handle);
  }

  /* ------------------------------------------------------------------ *
   * Line 1: block re-adding a sample that is already in the cart
   * ------------------------------------------------------------------ */

  interceptAddToCart() {
    // Capture phase, so this runs before product-form's own submit handler.
    // The decision must be synchronous: after an await, preventDefault() no
    // longer stops the event.
    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!form.matches('[action*="/cart/add"]') && !form.matches('[data-type="add-to-cart-form"]')) return;
        if (!this.isSampleForm(form)) return;

        const variantId = this.getFormVariantId(form);

        if (variantId && this.cartVariantIds.has(variantId)) {
          e.preventDefault();
          e.stopPropagation();
          this.showToast('Diese Probe ist bereits in deinem Warenkorb – max. 1 Stück pro Sorte.');
          return;
        }

        // Samples are always added one at a time. form.elements also covers
        // the PDP quantity input, which sits outside the <form> and is
        // associated via its form="" attribute.
        const quantityInput = form.elements['quantity'];
        if (quantityInput && parseInt(quantityInput.value, 10) > this.MAX_PER_SAMPLE) {
          quantityInput.value = String(this.MAX_PER_SAMPLE);
        }
      },
      true
    );
  }

  isSampleForm(form) {
    const productFormElement = form.closest('product-form');
    const tags = (productFormElement && productFormElement.dataset.productTags) || form.dataset.productTags || '';
    return tags.split(',').includes(this.SAMPLE_TAG);
  }

  getFormVariantId(form) {
    const idInput = form.elements['id'];
    return idInput && idInput.value ? String(idInput.value) : null;
  }

  /* ------------------------------------------------------------------ *
   * Line 2: clamp sample lines that exceeded the limit anyway
   * ------------------------------------------------------------------ */

  async validateCurrentCart(cart) {
    if (this.isClamping) return;

    const cartData = cart || (await this.getCurrentCart());
    if (!cartData || !cartData.items) return;

    const overLimit = [];
    for (let index = 0; index < cartData.items.length; index++) {
      const item = cartData.items[index];
      if (item.quantity > this.MAX_PER_SAMPLE && (await this.isSampleHandle(item.handle))) {
        overLimit.push({ line: index + 1, title: item.product_title });
      }
    }

    if (overLimit.length === 0) return;

    this.isClamping = true;
    try {
      for (const entry of overLimit) {
        await this.clampLineQuantity(entry.line);
      }
      this.showToast('Proben sind auf 1 Stück pro Sorte limitiert – die Menge wurde angepasst.');
    } finally {
      this.isClamping = false;
    }
  }

  async clampLineQuantity(line) {
    // Prefer the theme's cart component: it re-renders drawer/cart sections
    // and publishes cartUpdate itself.
    const cartItemsElement = document.querySelector('cart-items, cart-drawer-items');
    if (cartItemsElement && typeof cartItemsElement.updateQuantity === 'function') {
      cartItemsElement.updateQuantity(line, this.MAX_PER_SAMPLE);
      return;
    }

    // Fallback (no cart component on the page): change the line directly
    try {
      const response = await fetch(`${this.routesRoot}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: this.MAX_PER_SAMPLE }),
      });
      if (response.ok) {
        const cart = await response.json();
        this.cartVariantIds = new Set(cart.items.map((item) => String(item.variant_id)));
        this.updateSampleFormsUI();
      }
    } catch (error) {
      console.error('[sample-cart-validation] Error clamping quantity:', error);
    }
  }

  /* ------------------------------------------------------------------ *
   * Line 3: reflect the rule in the UI (PDP + collection quick-buy)
   * ------------------------------------------------------------------ */

  updateSampleFormsUI() {
    document.querySelectorAll('product-form[data-product-tags]').forEach((productFormElement) => {
      const tags = (productFormElement.dataset.productTags || '').split(',');
      if (!tags.includes(this.SAMPLE_TAG)) return;

      const form = productFormElement.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      if (!form) return;

      const variantId = this.getFormVariantId(form);
      const submitButton = form.querySelector('[type="submit"], button[name="add"]');
      if (!submitButton) return;

      const alreadyInCart = Boolean(variantId && this.cartVariantIds.has(variantId));

      if (alreadyInCart) {
        if (!submitButton.dataset.sampleOriginalText) {
          submitButton.dataset.sampleOriginalText = submitButton.textContent.trim();
        }
        submitButton.disabled = true;
        submitButton.classList.add('product-form__submit--sample-disabled');
        submitButton.textContent = 'Probe bereits im Warenkorb';
      } else if (submitButton.dataset.sampleOriginalText) {
        submitButton.disabled = false;
        submitButton.classList.remove('product-form__submit--sample-disabled');
        submitButton.textContent = submitButton.dataset.sampleOriginalText;
        delete submitButton.dataset.sampleOriginalText;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Toast notifications
   * ------------------------------------------------------------------ */

  createToastContainer() {
    this.toastContainer = document.createElement('div');
    this.toastContainer.className = 'sample-toast-container';
    document.body.appendChild(this.toastContainer);
  }

  showToast(message, type = 'error') {
    const toast = document.createElement('div');
    toast.className = `sample-toast sample-toast--${type}`;
    toast.innerHTML = `
      <div class="sample-toast__content">
        <span class="sample-toast__message"></span>
        <button class="sample-toast__close" aria-label="Schließen">&times;</button>
      </div>
    `;
    toast.querySelector('.sample-toast__message').textContent = message;

    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('sample-toast--fade-out');
      setTimeout(() => toast.remove(), 300);
    }, 5000);

    toast.querySelector('.sample-toast__close').addEventListener('click', () => {
      toast.remove();
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.sampleValidator = new SampleCartValidator();
  });
} else {
  window.sampleValidator = new SampleCartValidator();
}
